import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  CalendarRange,
  Flame,
  LayoutGrid,
  ListOrdered,
  ChevronLeft,
  ChevronRight,
  Keyboard,
  ListChecks,
  ListFilter,
  Plus,
  RefreshCw,
  Rows3,
  Search,
  X,
} from 'lucide-react'
import type { ActivityCode, ActivityState, CalendarActivity, PendingTask } from '@/api/resourceCalendarApi'
import { usePageTitle } from '@/components/layout/PageTitle'
import { ActivityDialog } from '@/components/my-tasks/ActivityDialog'
import { ActivityLegend } from '@/components/my-tasks/ActivityLegend'
import { TaskQueue } from '@/components/my-tasks/TaskQueue'
import { AgendaView } from '@/components/my-tasks/AgendaView'
import { MonthView } from '@/components/my-tasks/MonthView'
import { SummaryChips } from '@/components/my-tasks/SummaryChips'
import { WorkloadView } from '@/components/my-tasks/WorkloadView'
import {
  CALENDAR_VIEWS,
  addMonths,
  monthGrid,
  quickFilter,
  summaryStats,
  type CalendarViewId,
  type QuickFilter,
} from '@/lib/myTasksViews'
import { MoveDialog, type MoveTarget } from '@/components/my-tasks/MoveDialog'
import { PlanTaskDialog, type PlanPreset } from '@/components/my-tasks/PlanTaskDialog'
import { ResourceCalendar } from '@/components/my-tasks/ResourceCalendar'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useResourceCalendar, useTaskQueue } from '@/hooks/useResourceCalendar'
import { useQueryClient } from '@tanstack/react-query'
import {
  ACTIVITY_CODES,
  SHORTCUTS,
  STATE_LABELS,
  UNASSIGNED,
  activeFilterCount,
  dayHeader,
  filterActivities,
  overloadedOnly,
  shortcutFor,
  stepWindow,
  windowDays,
  type ActivityFilters,
  type ViewMode,
} from '@/lib/myTasks'
import { cn } from '@/lib/utils'

const ALL = '__all__'
const PREFS_KEY = 'cgt.myTasks.prefs'

interface Prefs {
  mode: ViewMode
  compact: boolean
}

/** Day/week and density survive a reload in this browser — a convenience only. */
function readPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    const p = raw ? (JSON.parse(raw) as Partial<Prefs>) : {}
    return { mode: p.mode === 'day' ? 'day' : 'week', compact: p.compact === true }
  } catch {
    return { mode: 'week', compact: false }
  }
}

function writePrefs(p: Prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p))
  } catch {
    /* storage blocked: the page still works, it just forgets */
  }
}

interface MoveState {
  activity: CalendarActivity
  initial: MoveTarget
  editable: boolean
  lockDate?: boolean
  title: string
}

const VIEW_ICONS = {
  board: LayoutGrid,
  agenda: ListOrdered,
  workload: Flame,
  month: CalendarRange,
} as const

type PageTab = 'calendar' | 'queue'

/**
 * My Tasks (z8nrz7fh6h). Two tabs: the calendar, full height, and — for a
 * supervisor — the equipment queue. Someone who is not a supervisor sees their
 * own row; a supervisor sees everyone and the Unassigned row, narrows to a
 * team, plans pending work and moves it. The server decides the scope; this
 * page renders what it is given.
 */
export function MyTasksPage() {
  usePageTitle('My Tasks')
  const [params, setParams] = useSearchParams()
  const tab: PageTab = params.get('tab') === 'queue' ? 'queue' : 'calendar'
  const askedView = params.get('view') as CalendarViewId | null
  const calView: CalendarViewId = CALENDAR_VIEWS.some((v) => v.id === askedView)
    ? (askedView as CalendarViewId)
    : 'board'
  const setCalView = (v: CalendarViewId) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (v === 'board') next.delete('view')
        else next.set('view', v)
        return next
      },
      { replace: true },
    )
  const setTab = (t: PageTab) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (t === 'calendar') next.delete('tab')
        else next.set('tab', t)
        return next
      },
      { replace: true },
    )

  const [prefs, setPrefsState] = useState<Prefs>(readPrefs)
  const setPrefs = (p: Partial<Prefs>) =>
    setPrefsState((cur) => {
      const next = { ...cur, ...p }
      writePrefs(next)
      return next
    })
  const { mode, compact } = prefs

  // Null until the user navigates: the server then picks the center's today.
  const [from, setFrom] = useState<string | null>(null)
  const [teamDraft, setTeamDraft] = useState(ALL)
  const [teamId, setTeamId] = useState<string | undefined>(undefined)
  const [filters, setFilters] = useState<ActivityFilters>({})
  const [onlyOverloaded, setOnlyOverloaded] = useState(false)
  const [quick, setQuick] = useState<QuickFilter>(null)
  const [opened, setOpened] = useState<CalendarActivity | null>(null)
  const [moving, setMoving] = useState<MoveState | null>(null)
  const [planning, setPlanning] = useState<PlanPreset | null>(null)
  const [planningTask, setPlanningTask] = useState<PendingTask | null>(null)
  const [notice, setNotice] = useState<string[]>([])
  const searchRef = useRef<HTMLInputElement>(null)

  const isMonth = calView === 'month'
  // The month grid needs the center's today before the first navigation.
  const [knownToday, setKnownToday] = useState<string | null>(null)
  const anchor = from ?? (isMonth ? knownToday : null)
  const grid = useMemo(() => (isMonth && anchor ? monthGrid(anchor) : null), [isMonth, anchor])
  const requested = grid ? [grid.from, grid.to] : from ? windowDays(from, mode) : null
  const calendar = useResourceCalendar({
    from: requested?.[0],
    to: requested?.[requested.length - 1],
    teamId,
  })
  const view = calendar.data
  useEffect(() => {
    if (view?.today && !knownToday) setKnownToday(view.today)
  }, [view?.today, knownToday])
  const firstDay = from ?? view?.days[0] ?? null
  const shownDays = useMemo(
    () => (grid ? grid.weeks.flat() : firstDay ? windowDays(firstDay, mode) : []),
    [grid, firstDay, mode],
  )
  const start = grid ? grid.first : (shownDays[0] ?? '')
  const today = view?.today ?? ''
  const step = (direction: 1 | -1) =>
    grid ? addMonths(grid.first, direction) : stepWindow(start, mode, direction)

  const all = view?.scope === 'all'
  const canManage = !!view?.canManage
  const queue = useTaskQueue(!!view)
  // The tab's badge: what nobody owns (supervisor), or what is yours (anyone else).
  const queueCount = queue.data
    ? all
      ? queue.data.filter((t) => t.ownerId === null).length
      : queue.data.length
    : undefined
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ['resource-calendar'] })

  const people = useMemo(
    () => (view?.resources ?? []).filter((r) => r.active).map((r) => ({ userId: r.userId, name: r.name })),
    [view],
  )
  const nameOf = (id: string | null) =>
    id === null ? 'Unassigned' : (view?.resources.find((r) => r.userId === id)?.name ?? 'Someone else')
  const legendName = (code: ActivityCode) => view?.legend.find((l) => l.code === code)?.name ?? code

  const inWindow = useMemo(
    () => (view?.activities ?? []).filter((a) => shownDays.includes(a.date)),
    [view, shownDays],
  )
  const shown = useMemo(
    () => quickFilter(filterActivities(inWindow, filters), quick, today),
    [inWindow, filters, quick, today],
  )
  const stats = useMemo(
    () => summaryStats(view?.resources ?? [], inWindow, today),
    [view, inWindow, today],
  )
  const resources = useMemo(() => {
    let rows = view?.resources ?? []
    if (onlyOverloaded) rows = overloadedOnly(rows)
    if (filters.resourceId) rows = rows.filter((r) => r.userId === filters.resourceId)
    return rows
  }, [view, onlyOverloaded, filters.resourceId])
  const showUnassigned = all && !onlyOverloaded && (!filters.resourceId || filters.resourceId === UNASSIGNED)
  const filterCount = activeFilterCount(filters) + (onlyOverloaded ? 1 : 0)

  const setFilter = <K extends keyof ActivityFilters>(k: K, v: ActivityFilters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }))

  const go = useCallback((to: string) => setFrom(to), [])
  const openDay = (date: string, userId?: string) => {
    setPrefs({ mode: 'day' })
    setFrom(date)
    if (userId) setFilters((f) => ({ ...f, resourceId: userId }))
    setCalView('board')
  }
  const switchMode = useCallback(
    (m: ViewMode) => {
      setPrefs({ mode: m })
      if (start) setFrom(start)
    },
    // setPrefs only calls the state setter and storage, both stable.
    [start],
  )

  // Keyboard: ← → T D W / N — never while typing or with a dialog open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || tab !== 'calendar') return
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return
      if (opened || moving || planning || document.querySelector('[role="dialog"]')) return
      const action = shortcutFor(e.key)
      if (!action || !view) return
      e.preventDefault()
      if (action === 'prev' && start) go(step(-1))
      else if (action === 'next' && start) go(step(1))
      else if (action === 'board' || action === 'agenda' || action === 'workload' || action === 'month')
        setCalView(action)
      else if (action === 'today') go(view.today)
      else if ((action === 'day' || action === 'week') && !isMonth) switchMode(action)
      else if (action === 'search') searchRef.current?.focus()
      else if (action === 'add' && canManage) setPlanning({})
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tab, view, start, mode, opened, moving, planning, canManage, go, switchMode, isMonth, grid]) // eslint-disable-line react-hooks/exhaustive-deps

  // Equipment / cleaning from the queue: change the owner on the task itself,
  // keeping its day. The queue row carries the key and lock token for that.
  const openAssign = (t: PendingTask) =>
    setMoving({
      activity: {
        key: t.key as string,
        type: t.type,
        description: t.description,
        source: 'maintenance',
        sourceId: t.id,
        sourceRef: t.sourceRef,
        href: t.href,
        ownerId: t.ownerId,
        date: t.plannedDate ?? t.dueDate ?? view?.today ?? '',
        dueDate: t.dueDate,
        hours: t.hours,
        startTime: null,
        endTime: null,
        state: 'PLANNED',
        version: t.version as string,
        movable: true,
      },
      initial: { date: t.plannedDate ?? t.dueDate ?? view?.today ?? '', ownerId: t.ownerId },
      editable: true,
      lockDate: true,
      title: t.ownerId ? 'Reassign task' : 'Assign task',
    })

  const rangeLabel = shownDays.length
    ? shownDays.length > 1
      ? `${dayHeader(shownDays[0])} – ${dayHeader(shownDays[shownDays.length - 1])}`
      : dayHeader(shownDays[0])
    : ''

  return (
    <div className='flex h-full min-h-0 flex-col gap-3 bg-muted/30 px-4 py-4 sm:px-6'>
      {/* Title, tabs and the one primary action */}
      <header className='flex flex-wrap items-center gap-x-6 gap-y-2'>
        <h1 className='text-2xl font-semibold tracking-tight'>My Tasks</h1>
        {view && (
          <nav role='tablist' aria-label='My Tasks views' className='flex gap-1 rounded-lg bg-card p-1 shadow-sm ring-1 ring-border'>
            {(
              [
                { id: 'calendar', label: 'Calendar', icon: CalendarDays },
                { id: 'queue', label: all ? 'Task queue' : 'My pending tasks', icon: ListChecks },
              ] as const
            ).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type='button'
                role='tab'
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground',
                  tab === id && 'bg-primary/10 font-medium text-primary',
                )}
              >
                <Icon className='h-4 w-4' />
                {label}
                {id === 'queue' && !!queueCount && (
                  <span className='rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary'>
                    {queueCount}
                  </span>
                )}
              </button>
            ))}
          </nav>
        )}
        {canManage && tab === 'calendar' && (
          <Button className='ml-auto' onClick={() => setPlanning({})}>
            <Plus className='mr-1 h-4 w-4' /> Plan a task
          </Button>
        )}
      </header>

      {tab === 'queue' && view ? (
        <TaskQueue
          scope={view.scope}
          canManage={canManage}
          legend={view.legend}
          onAssign={openAssign}
          onPlan={(t) => {
            setPlanningTask(t)
            setPlanning({ ownerId: t.ownerId })
          }}
        />
      ) : (
        <>
          {/* One toolbar: where you are, how you look at it, what you see */}
          <div className='flex flex-wrap items-center gap-2'>
            <div className='flex items-center rounded-lg bg-card p-0.5 shadow-sm ring-1 ring-border'>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                aria-label={isMonth ? 'Previous month' : mode === 'week' ? 'Previous 7 days' : 'Previous day'}
                title='Previous (←)'
                disabled={!start}
                onClick={() => go(step(-1))}
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                className='h-8 px-3 text-sm'
                title='Today (T)'
                disabled={!view}
                onClick={() => view && go(view.today)}
              >
                Today
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                aria-label={isMonth ? 'Next month' : mode === 'week' ? 'Next 7 days' : 'Next day'}
                title='Next (→)'
                disabled={!start}
                onClick={() => go(step(1))}
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>

            <label className='relative flex items-center'>
              <span className='sr-only'>Jump to a date</span>
              <input
                type='date'
                value={start}
                onChange={(e) => e.target.value && go(e.target.value)}
                className='h-9 rounded-lg bg-card px-3 text-sm font-medium shadow-sm ring-1 ring-border focus:outline-none focus:ring-2 focus:ring-primary/40'
                title='Jump to a date'
              />
            </label>
            <span className='hidden text-sm font-medium text-foreground lg:inline'>{grid ? grid.label : rangeLabel}</span>

            {/* The four views */}
            <div role='tablist' aria-label='Calendar view' className='flex rounded-lg bg-card p-0.5 shadow-sm ring-1 ring-border'>
              {CALENDAR_VIEWS.map((v, i) => {
                const Icon = VIEW_ICONS[v.id]
                return (
                  <button
                    key={v.id}
                    type='button'
                    role='tab'
                    aria-selected={calView === v.id}
                    title={`${v.hint} (${i + 1})`}
                    onClick={() => setCalView(v.id)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground',
                      calView === v.id && 'bg-primary/10 font-medium text-primary',
                    )}
                  >
                    <Icon className='h-3.5 w-3.5' />
                    <span className='hidden sm:inline'>{v.label}</span>
                  </button>
                )
              })}
            </div>

            {!isMonth && (
            <div role='group' aria-label='Range' className='flex rounded-lg bg-card p-0.5 shadow-sm ring-1 ring-border'>
              {(['day', 'week'] as const).map((m) => (
                <button
                  key={m}
                  type='button'
                  aria-pressed={mode === m}
                  title={m === 'day' ? 'Day (D)' : 'Week (W)'}
                  onClick={() => switchMode(m)}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground',
                    mode === m && 'bg-primary/10 font-medium text-primary',
                  )}
                >
                  {m === 'day' ? 'Day' : 'Week'}
                </button>
              ))}
            </div>
            )}

            <div className='ml-auto flex flex-wrap items-center justify-end gap-2'>
              {view && (
                <SummaryChips
                  stats={stats}
                  showTeamStats={all}
                  quick={quick}
                  onlyOverloaded={onlyOverloaded}
                  onQuick={setQuick}
                  onOverloaded={setOnlyOverloaded}
                />
              )}
              {all && (
                <div className='flex items-center gap-1 rounded-lg bg-card p-0.5 shadow-sm ring-1 ring-border'>
                  <Select value={teamDraft} onValueChange={setTeamDraft}>
                    <SelectTrigger className='h-8 w-40 border-0 shadow-none' aria-label='Team'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>All teams</SelectItem>
                      {view?.teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size='sm' className='h-8' onClick={() => setTeamId(teamDraft === ALL ? undefined : teamDraft)}>
                    Go
                  </Button>
                </div>
              )}

              <label className='flex h-9 items-center gap-2 rounded-lg bg-card px-3 shadow-sm ring-1 ring-border focus-within:ring-2 focus-within:ring-primary/40'>
                <Search className='h-4 w-4 text-muted-foreground' />
                <input
                  ref={searchRef}
                  aria-label='Search description or reference'
                  placeholder='Search  /'
                  value={filters.search ?? ''}
                  onChange={(e) => setFilter('search', e.target.value)}
                  className='w-40 bg-transparent text-sm outline-none placeholder:text-muted-foreground'
                />
              </label>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant='outline' className={cn('h-9 gap-1.5 bg-card', filterCount > 0 && 'border-primary/40 text-primary')}>
                    <ListFilter className='h-4 w-4' /> Filters
                    {filterCount > 0 && (
                      <span className='rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums'>{filterCount}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align='end' className='w-72 space-y-3'>
                  {all && (
                    <div className='space-y-1'>
                      <Label>Resource</Label>
                      <Select
                        value={filters.resourceId ?? ALL}
                        onValueChange={(v) => setFilter('resourceId', v === ALL ? undefined : v)}
                      >
                        <SelectTrigger aria-label='Resource'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>Everyone</SelectItem>
                          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                          {view?.resources.map((r) => (
                            <SelectItem key={r.userId} value={r.userId}>
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className='space-y-1'>
                    <Label>Type</Label>
                    <Select
                      value={filters.type ?? ALL}
                      onValueChange={(v) => setFilter('type', v === ALL ? undefined : (v as ActivityCode))}
                    >
                      <SelectTrigger aria-label='Activity type'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All types</SelectItem>
                        {ACTIVITY_CODES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c} · {legendName(c)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-1'>
                    <Label>State</Label>
                    <Select
                      value={filters.state ?? ALL}
                      onValueChange={(v) => setFilter('state', v === ALL ? undefined : (v as ActivityState))}
                    >
                      <SelectTrigger aria-label='State'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All states</SelectItem>
                        {(Object.keys(STATE_LABELS) as ActivityState[]).map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATE_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {all && (
                    <label className='flex items-center justify-between gap-3 text-sm'>
                      Only people over capacity
                      <Switch checked={onlyOverloaded} onCheckedChange={setOnlyOverloaded} />
                    </label>
                  )}
                  {filterCount > 0 && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='w-full'
                      onClick={() => {
                        setFilters({})
                        setOnlyOverloaded(false)
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                  <p className='text-xs text-muted-foreground'>Filters hide cards; each day’s load still counts everything.</p>
                </PopoverContent>
              </Popover>

              <Button
                variant='outline'
                size='icon'
                className='h-9 w-9 bg-card'
                aria-label='Refresh'
                title='Refresh'
                onClick={refresh}
              >
                <RefreshCw className={cn('h-4 w-4', calendar.isFetching && 'animate-spin')} />
              </Button>
              <Button
                variant='outline'
                size='icon'
                className={cn('h-9 w-9 bg-card', compact && 'border-primary/40 bg-primary/10 text-primary')}
                aria-pressed={compact}
                aria-label='Compact rows'
                title='Compact rows'
                onClick={() => setPrefs({ compact: !compact })}
              >
                <Rows3 className='h-4 w-4' />
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant='outline' size='icon' className='h-9 w-9 bg-card' aria-label='Keyboard shortcuts' title='Keyboard shortcuts'>
                    <Keyboard className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align='end' className='w-60'>
                  <p className='mb-2 text-sm font-medium'>Keyboard shortcuts</p>
                  <ul className='space-y-1.5 text-sm'>
                    {SHORTCUTS.filter((s) => s.action !== 'add' || canManage).map((s) => (
                      <li key={s.keys} className='flex items-center justify-between gap-3'>
                        <span className='text-muted-foreground'>{s.label}</span>
                        <kbd className='rounded border bg-muted px-1.5 py-0.5 text-xs font-medium'>{s.keys}</kbd>
                      </li>
                    ))}
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {notice.length > 0 && (
            <div className='flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
              <ul>
                {notice.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
              <button type='button' aria-label='Dismiss' onClick={() => setNotice([])}>
                <X className='h-4 w-4' />
              </button>
            </div>
          )}

          {calendar.isLoading ? (
            <div className='flex flex-1 items-center justify-center rounded-2xl border bg-card text-sm text-muted-foreground'>
              Loading the calendar…
            </div>
          ) : calendar.isError ? (
            <div className='rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
              {calendar.error.message}
            </div>
          ) : view && calView === 'agenda' ? (
            <AgendaView
              days={shownDays}
              today={view.today}
              activities={shown}
              resources={view.resources}
              canManage={canManage}
              onOpen={setOpened}
              onAdd={setPlanning}
            />
          ) : view && calView === 'workload' ? (
            <WorkloadView
              days={shownDays}
              today={view.today}
              resources={resources}
              onPick={(userId, date) => openDay(date, userId)}
            />
          ) : view && grid ? (
            <MonthView
              weeks={grid.weeks}
              monthFirst={grid.first}
              today={view.today}
              activities={shown}
              onPick={(date) => openDay(date)}
            />
          ) : view ? (
            <ResourceCalendar
              days={shownDays}
              today={view.today}
              resources={resources}
              activities={shown}
              allActivities={inWindow}
              showUnassigned={showUnassigned}
              canManage={canManage}
              compact={compact}
              onOpen={setOpened}
              onAdd={setPlanning}
              onPropose={(activity, target) =>
                setMoving({ activity, initial: target, editable: false, title: 'Confirm move' })
              }
              onMove={(activity) =>
                setMoving({
                  activity,
                  initial: { date: activity.date, ownerId: activity.ownerId },
                  editable: true,
                  title: 'Change day or owner',
                })
              }
            />
          ) : null}

          {view && <ActivityLegend legend={view.legend} />}
        </>
      )}

      {opened && (
        <ActivityDialog
          activity={opened}
          legendName={legendName(opened.type)}
          ownerName={nameOf(opened.ownerId)}
          canManage={canManage}
          onClose={() => setOpened(null)}
          onMove={() => {
            setMoving({
              activity: opened,
              initial: { date: opened.date, ownerId: opened.ownerId },
              editable: true,
              title: 'Change day or owner',
            })
            setOpened(null)
          }}
        />
      )}
      {moving && <MoveDialog {...moving} people={people} ownerName={nameOf} onClose={() => setMoving(null)} />}
      {planning && view && (
        <PlanTaskDialog
          key={planningTask?.id ?? 'new'}
          preset={planning}
          initialTask={planningTask}
          today={view.today}
          legend={view.legend}
          people={people}
          onClose={() => {
            setPlanning(null)
            setPlanningTask(null)
          }}
          onWarnings={setNotice}
        />
      )}
    </div>
  )
}
